import { Box, TextField, Tooltip, Typography } from '@mui/material';
import Table from 'components/Table';
import {
  dateToLabel,
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import {
  InvestmentDetails as InvestmentDetailsType,
  ShareHolding,
} from 'hooks/data/investmentTransfer/useInvestmentTransfer';
import { sum, sumBy } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import useStyles from './styles';

type InvestmentsDetailsProps = {
  investment: InvestmentDetailsType;
};

const staticColumns = [
  {
    label: 'Company',
    key: 'company',
    render: (shareholding: ShareHolding) => shareholding.company.tradingName,
  },
  {
    label: 'Subscription date',
    key: 'date',
    render: (shareholding: ShareHolding) => dateToLabel(shareholding.date),
  },
  {
    label: 'Initial share price',
    key: 'initialPrice',
    render: (shareholding: ShareHolding) =>
      numberToCurrencyString(shareholding.initialSharePrice),
  },
  {
    label: 'Shares held',
    key: 'noOfShare',
    render: (shareholding: ShareHolding) =>
      numberToDisplayString(shareholding.noOfShare),
  },
  {
    label: 'Initial subscription',
    key: 'subscriptionAmount',
    render: (shareholding: ShareHolding) =>
      numberToCurrencyString(shareholding.subscriptionAmount),
  },
  {
    label: 'Current share price',
    key: 'currentSharePrice',
    render: (shareholding: ShareHolding) =>
      numberToCurrencyString(shareholding.currentSharePrice),
  },
  {
    label: 'Current share value',
    key: 'currentShareValue',
    render: (shareholding: ShareHolding) =>
      numberToCurrencyString(shareholding.currentShareValue),
  },
];

const InvestmentDetails: React.FC<InvestmentsDetailsProps> = ({
  investment,
}) => {
  const { classes } = useStyles();
  const { control, watch } = useFormContext();

  const columns = [
    ...staticColumns,
    {
      label: 'Shares to transfer',
      key: 'sharesToTransfer',
      render: (shareholding: ShareHolding) => (
        <Controller
          name={`sharesToTransfer[${investment.investmentId}][${shareholding.id}]`}
          defaultValue={0}
          control={control}
          render={({ field: { ref, ...rest }, fieldState: { error } }) => (
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              title={error?.message ?? ''}
            >
              <TextField
                inputRef={ref}
                type="number"
                variant="outlined"
                error={!!error}
                {...rest}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Value to transfer',
      key: 'valueToTransfer',
      render: (shareholding: ShareHolding) => {
        const sharesToTranfer = watch(
          `sharesToTransfer[${investment.investmentId}][${shareholding.id}]`,
        );
        return numberToCurrencyString(
          shareholding.currentSharePrice * (sharesToTranfer ?? 0),
        );
      },
    },
  ];

  const liveShareholdings = investment.shareholdings.filter(
    (shareholding) => shareholding.noOfShare > 0,
  );

  const getSharesToTransferValue = () => {
    return sum(
      liveShareholdings.map(
        (shareholding) =>
          (shareholding.currentSharePrice ?? 0) *
          (watch(
            `sharesToTransfer[${investment.investmentId}][${shareholding.id}]`,
          ) ?? 0),
      ),
    );
  };

  if (!investment.availableForTransfer) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        gap={20}
        alignItems="center"
        py={8}
        className={classes.transferNotAvailableMessage}
      >
        <Typography variant="h1" align="center">
          This subscription is still active
        </Typography>
        <Box maxWidth={550} display="flex" justifyContent="center">
          <Typography variant="body1" align="center">
            To transfer any holdings from this subscription, please first freeze
            the subscription and withdraw all this subscription's uninvested
            capital.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Table
      columns={columns}
      tablebody={liveShareholdings}
      TableFooter={
        <tfoot>
          <tr className={classes.tableFooter}>
            <td colSpan={4}>
              <Typography
                variant="h6"
                component="div"
                align="right"
                className={classes.totalValueLabel}
              >
                Subscription value
              </Typography>
            </td>
            <td>
              <Typography variant="h6" component="div">
                {numberToCurrencyString(
                  sumBy(liveShareholdings, (shareholding) =>
                    Number(shareholding.subscriptionAmount),
                  ),
                )}
              </Typography>
            </td>
            <td>
              <Typography
                variant="h6"
                component="div"
                align="right"
                className={classes.totalValueLabel}
              >
                Total value
              </Typography>
            </td>
            <td>
              <Typography variant="h6" component="div">
                {numberToCurrencyString(
                  sumBy(liveShareholdings, 'currentShareValue'),
                )}
              </Typography>
            </td>
            <td>
              <Typography
                variant="h6"
                component="div"
                align="right"
                className={classes.totalValueLabel}
              >
                Initial value to transfer
              </Typography>
            </td>
            <td>
              <Typography variant="h6" component="div">
                {numberToCurrencyString(getSharesToTransferValue())}
              </Typography>
            </td>
          </tr>
        </tfoot>
      }
    />
  );
};

export default InvestmentDetails;
